import type { Vec3 } from './model.js';
import type { RoomWallBuild } from './room.js';
import { clipBox } from './slope.js';

/**
 * A wall as a solid, cut by the roof like a boolean: convex pieces in the wall's own frame
 * (u along the wall from its start, y up, d through the thickness outwards from the inner face; µm).
 * The footprint is the wall between its inner and outer face with the mitred ends; openings split it
 * into pieces below and above them; every piece is cut by the roof planes over the wall — the planes
 * through its top line and each joined wall's top line — so a sloping roof cuts the low wall across
 * its thickness and the gable walls along their length, with flat faces.
 */
type Plane = { n: Vec3; c: number };
type Lin = { pu: number; pd: number; p0: number };

const MM = 1000;

/** A ceiling plane (room coordinates) as a linear height over (u, d) of this wall. */
function local(w: RoomWallBuild, cl: { a: number; b: number; c: number }): Lin {
  return { pu: cl.a * w.ux + cl.b * w.uz, pd: cl.a * w.nx + cl.b * w.nz, p0: cl.a * w.ax + cl.b * w.az + cl.c };
}
const under = (l: Lin): Plane => ({ n: [-l.pu, 1, -l.pd], c: l.p0 });

export function wallSolidPieces(w: RoomWallBuild): Vec3[][] {
  const L = w.length;
  const T = w.thickness;
  const own = local(w, w.ceil);
  const base: Plane[] = [
    { n: [0, 0, -1], c: 0 },
    { n: [0, 0, 1], c: T },
    { n: [0, -1, 0], c: 0 },
    // Mitred ends: the inner face ends in the points, the outer face runs on by extA / extB.
    { n: [-1, 0, -(w.extA / (T || 1))], c: 0 },
    { n: [1, 0, -(w.extB / (T || 1))], c: L },
  ];
  // The roof: the planes through this wall and each joined wall (both contain the wall's top line);
  // the wall is cut by all of them. Without a joined wall: its own plane.
  const roofs = [w.ceilA, w.ceilB].filter((c): c is NonNullable<typeof c> => !!c).map((c) => under(local(w, c)));
  const top = roofs.length ? roofs : [under(own)];
  // Pieces along the wall: openings' edges and the ends of the corner zones.
  const cuts = new Set<number>();
  for (const h of w.holes) {
    cuts.add(h.u0);
    cuts.add(h.u1);
  }
  const xs = [...cuts].filter((u) => u > 0 && u < L).sort((a, b) => a - b);
  // The corner blocks (beyond the inner corners, u < 0 and u > L) are pieces of their own: they are
  // shared with the joined wall and cut by its roof planes too, so both walls end in the same top.
  const bounds = [-Infinity, 0, ...xs, L, Infinity];
  const nbTop = (cl?: { a: number; b: number; c: number }[]) => (cl ?? []).map((c) => under(local(w, c)));
  const lo: Vec3 = [-w.extA - 1_000_000, 0, 0];
  const hi: Vec3 = [L + w.extB + 1_000_000, 100_000_000, T];
  const out: Vec3[][] = [];
  const piece = (planes: Plane[]) => {
    const pts = clipBox(lo, hi, planes);
    if (pts.length < 4) return;
    const span = [0, 1, 2].map((k) => Math.max(...pts.map((p) => p[k]!)) - Math.min(...pts.map((p) => p[k]!)));
    if (span.every((v) => v > MM / 2)) out.push(pts);
  };
  for (let i = 0; i + 1 < bounds.length; i++) {
    const a = bounds[i]!;
    const b = bounds[i + 1]!;
    const along: Plane[] = [...(Number.isFinite(a) ? [{ n: [-1, 0, 0] as Vec3, c: -a }] : []), ...(Number.isFinite(b) ? [{ n: [1, 0, 0] as Vec3, c: b }] : [])];
    const mid = Number.isFinite(a) && Number.isFinite(b) ? (a + b) / 2 : Number.isFinite(b) ? b - MM : a + MM;
    // Openings over this stretch: below and above them.
    // (The stretches never straddle an opening's edge: those edges are cut points.)
    const hole = w.holes.find((h) => mid > h.u0 && mid < h.u1);
    const ranges: Plane[][] = hole ? [[{ n: [0, 1, 0], c: hole.y0 }], [{ n: [0, -1, 0], c: -hole.y1 }]] : [[]];
    const corner = !Number.isFinite(a) ? nbTop(w.nbA) : !Number.isFinite(b) ? nbTop(w.nbB) : [];
    for (const yr of ranges) piece([...base, ...along, ...yr, ...top, ...corner]);
  }
  return out;
}
